import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SupermoveStats } from "./types";

const WINDOW_DAYS = 30;

const joinUrl = (base: string, path: string) => {
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return `${normalizedBase}${normalizedPath}`;
};

const statsUrl = () => joinUrl(process.env.REACT_APP_API_URL ?? "", "supermove/stats");

const loadPersisted = () => {
  const rawKey = localStorage.getItem("supermoveStats.apiKey");
  const apiKey = rawKey ?? "";
  return { apiKey };
};

const persist = (apiKey: string) => {
  localStorage.setItem("supermoveStats.apiKey", apiKey);
};

const SupermoveStatsPage: React.FC = () => {
  const persisted = useMemo(() => loadPersisted(), []);
  const [apiKey, setApiKey] = useState<string>(persisted.apiKey);
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [stats, setStats] = useState<SupermoveStats | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError("");
    persist(apiKey);

    try {
      const res = await axios.get<SupermoveStats>(statsUrl(), {
        params: { days: WINDOW_DAYS },
        headers: apiKey ? { "x-api-key": apiKey } : undefined,
      });
      setStats(res.data);
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load Supermove stats";
      setError(String(message));
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recentFiltered = useMemo(() => {
    const rows = stats?.recent ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.identifier} ${r.phoneNumber ?? ""}`.toLowerCase().includes(q),
    );
  }, [filter, stats]);

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "flex-end" }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
            Supermove Stats
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recent <code>supermove</code> rows (basic view)
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ xs: "stretch", sm: "flex-end" }}>
          <TextField
            size="small"
            label="API key (optional)"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            sx={{ minWidth: 260 }}
          />

          <Button variant="contained" onClick={refresh} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} gap={1.5} sx={{ mt: 2 }}>
        <TextField
          size="small"
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="identifier / phone"
          sx={{ minWidth: 320 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
          API <code>GET /supermove/stats?days={WINDOW_DAYS}</code>
        </Typography>
      </Stack>

      <Box sx={{ mt: 1.5, borderRadius: 1.5, overflow: "auto", border: (t) => `1px solid ${t.palette.divider}` }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Phone</TableCell>
              <TableCell>Identifier</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Inventory</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentFiltered.slice(0, 200).map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.phoneNumber ?? "—"}</TableCell>
                <TableCell>
                  <code>{r.identifier}</code>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(r.createdAt).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  {r.hasInventory ? (
                    <Chip size="small" color="success" label="Yes" />
                  ) : (
                    <Chip size="small" variant="outlined" label="No" />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!recentFiltered.length ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No rows.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Showing {Math.min(recentFiltered.length, 200)} of {recentFiltered.length} rows · Generated at{" "}
        {stats ? new Date(stats.generatedAt).toLocaleString() : "—"}
      </Typography>
    </Box>
  );
};

export default SupermoveStatsPage;
